/**
 * Poppins is the platform's only typeface, self-hosted rather than pulled from
 * the Google CDN: this is an internal tool on a single VPS, and a warehouse
 * tablet on a flaky connection should not be waiting on fonts.gstatic.com to
 * render a build sheet.
 */
import '@fontsource/poppins/300.css';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';
/* JetBrains Mono: serials, references and table prices — read digit by digit. */
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/lib/auth';
import { ToastProvider } from '@/components/ui/overlays';
import { App } from './App';
import { initTheme } from '@/lib/theme';
import './index.css';
import './lumina.css';

// Resolve light/dark before the first paint — no flash of the wrong mode.
initTheme();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Internal tool with a handful of users: a short stale window keeps stock
      // and trial counts current without hammering the API.
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status;
        // Never retry auth or permission failures — they will not resolve.
        if (status === 401 || status === 403 || status === 404) return false;
        return failureCount < 2;
      },
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <App />
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
